import { Clickable, Col, Divider, Icon, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import { theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: ${(props) => (props.contain ? '100%' : props.isMobile ? '286px' : '480px')};
  position: relative;
  ${(props) => props.active && 'z-index: 10;'}
  ${(props) => props.marg && `margin: ${props.marg};`}
`;

const AppWrapper = styled.div`
  margin: ${(props) => props.marg || 0};
  width: ${(props) => props.wid || 'calc(100% - 32px)'};
  display: flex;
  height: 43px;
  padding: ${(props) => props.pad || '0 16px 8px'};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: ${(props) => (props.noBorder ? '0' : '1px')} solid ${theme.GREY_40};
`;

const AppIconContainer = styled.div`
  margin: 0 0 0 17px;

  ${(props) =>
    props.active &&
    `
    transform: rotate(180deg);
  `}
`;

const OptionsWrapper = styled.div`
  position: absolute;
  max-height: ${(props) => (props.maxHt || props.admin ? '350px' : '176px')};
  width: 100%;
  right: 0;
  top: ${(props) => props.top || '37px'};
  background: #fff;
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.2);
  overflow-y: scroll;
  scrollbar-width: none;
  padding: 12px 0px;
  z-index: 2;

  ${(props) =>
    props.rounded &&
    `
    padding-bottom: 12px;
    & > :last-child {
       margin-bottom: 12px;
    }
  `}

  ${(props) =>
    props.admin &&
    `
    width: 100%;
    border-radius: 8px;
  `}

  ${(props) =>
    props.upwards &&
    `
    top: unset;
    bottom: 0;
  `}
`;

const StyledDropdown = styled.div`
  height: ${(props) => (props.isMobile ? '39px' : '54px')};
  width: ${(props) => (props.contain ? '100%' : props.isMobile ? '286px' : '480px')};
  border-radius: ${(props) => (props.admin ? '15px' : '26px')};
  background-color: #fff;
  font-family: CeraPro;
  font-weight: 500;
  font-size: ${(props) => (props.isMobile ? '18px' : '22px')};
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.left ? 'flex-start' : 'center')};
  z-index: 3;
`;

const IconContaier = styled.div`
  position: absolute;
  right: ${(props) => (props.isMobile ? '25px' : '35px')};
  height: ${(props) => (props.isMobile ? '39px' : '54px')};
  display: flex;
  align-items: center;

  ${(props) =>
    props.active &&
    `
    transform: rotate(180deg);
  `}
`;

const Options = styled.div`
  left: 0;
  position: absolute;
  top: calc(100% - ${(props) => (props.isMobile ? '39px' : '54px')});
  width: 100%;
  border-radius: 26px;
  overflow-y: scroll;
  max-height: ${(props) => (props.isMobile ? '160px' : '220px')};
  background-color: #fff;
  z-index: 2;
`;

const Option = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.left ? 'flex-start' : 'center')};
  height: ${(props) => (props.isMobile ? '39px' : '54px')};
`;

const DropDown = (props) => {
  const {
    handleSelect,
    title,
    selected,
    value,
    translatePrefix,
    noSelectedIdx,
    textOverload,
    optColor
  } = props;

  const [active, setActive] = useState(false),
    [selectedIdx, setSelectedIdx] = useState(null),
    { isMobile } = useSelector((state) => state.appState),
    { t } = useTranslation(),
    wrapperRef = useRef(null),
    scrollRef = useRef(null),
    roundedOptionsRef = useRef(null);

  useEffect(() => {
    scrollRef?.current?.scroll({ y: 0 });
  }, [active]);

  // The rounded list is absolutely positioned, so near the bottom of a scrolling panel it opens
  // half below the fold. `nearest` scrolls only as far as needed, and not at all when it fits.
  useEffect(() => {
    if (active) roundedOptionsRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [active]);

  useEffect(() => {
    setActive(props.active);
  }, [props.active]);

  useOutsideClick(wrapperRef, () => active && setActive(false), props.ignoreOutside);

  function handleActive() {
    setActive(!active);
  }

  function getText(item) {
    // If the dropdown should use a different logic to get it's title
    if (textOverload) return textOverload(item);

    const text = typeof item === 'string' ? item : item.value || item.description || item.name;

    return translatePrefix ? t(`${translatePrefix}.${text}`) : text;
  }

  function getSelected() {
    const text =
      typeof selected === 'string'
        ? selected
        : selected.value || selected.description || selected.name;

    return translatePrefix ? t(`${translatePrefix}.${text}`) : text;
  }

  function handleClick(opt, i) {
    setActive(false);
    handleSelect(opt, i);
    !noSelectedIdx && setSelectedIdx(i);
  }

  if (props.website)
    return (
      <Wrapper marg={props.marg} active={active} ref={wrapperRef} isMobile={isMobile} {...props}>
        <StyledDropdown
          isMobile={isMobile}
          onClick={!props.disabled && handleActive}
          active={active}
          {...props}
        >
          <Text
            col={props.disabled ? theme.GREY_10 : theme.PRIMARY_BLACK}
            size={props.isMobile || isMobile ? '18px' : '22px'}
            marg={isMobile ? '0 20px' : '0 35px'}
          >
            {value || selected
              ? typeof props.selected === 'string'
                ? selected
                : selected.value
              : title}
          </Text>

          <IconContaier isMobile={isMobile} active={active}>
            <Icon
              type="downThin"
              dimensions={isMobile ? '13.11px' : '22px'}
              col={props.disabled && theme.GREY_10}
            />
          </IconContaier>
        </StyledDropdown>

        {active && (
          <Options active={active} ref={scrollRef} isMobile={isMobile}>
            <Option isMobile={isMobile}></Option>

            {props?.options?.map((opt, i) => (
              <Col key={i}>
                <Option onClick={() => handleClick(opt, i)} isMobile={isMobile} {...props}>
                  <Text
                    size={props.isMobile || isMobile ? '18px' : '22px'}
                    marg={isMobile ? '0 20px' : '0 35px'}
                  >
                    {getText(opt)}
                  </Text>
                </Option>

                <Divider />
              </Col>
            ))}
          </Options>
        )}
      </Wrapper>
    );

  if (props.rounded) {
    return (
      <Col wid={props.wid || '100%'} ref={wrapperRef} marg={props.marg} noFlex>
        {props.title && (
          <Text b2 med marg={props.admin ? '0 16px 4px' : '0 0 8px'}>
            {props.title || t('common.missing')}
          </Text>
        )}

        <Clickable onClick={props.onClick || handleActive}>
          <Row
            ht={props.small ? '41px' : '44px'}
            wid={'calc(100% - 34px)'}
            noFlex
            between
            center
            hasBorder={`${props.error ? '2px' : '1px'} solid ${props.error ? theme.SECONDARY : theme.GREY_50}`}
            hasRadius="48px"
            pad="0 18px 0 16px"
            bg={props.bg || 'transparent'}
          >
            <Text col={selected ? '#000' : theme.GREY_60}>
              {selected ? getSelected() : props.placeholder}
            </Text>

            <AppIconContainer active={active}>
              <Icon type="down" dimensions={12} col={theme.GREY_50} />
            </AppIconContainer>
          </Row>
        </Clickable>

        {props.error && (
          <Row start noFlex>
            <Text c1 bold marg="4px 0 0 16px" col={theme.SECONDARY}>
              {props.error}
            </Text>
          </Row>
        )}

        {active && (
          <Col pos="relative" wid="100%" className="dropdown-wrapper">
            <OptionsWrapper top="0px" ref={roundedOptionsRef} {...props}>
              {props.options?.map((x, i) => {
                const isSelected = selectedIdx === i;

                return (
                  <Clickable onClick={() => handleClick(x, i)} key={i} className="dropdown-item">
                    <Row between pad="11px 16px" wid="calc(100% - 32px)" className="dropdown-item">
                      <Text b1 med className="dropdown-item" wid="100%">
                        {getText(x)}
                      </Text>

                      {isSelected && <Icon type="done" col={theme.SECONDARY} />}
                    </Row>
                  </Clickable>
                );
              })}
            </OptionsWrapper>
          </Col>
        )}
      </Col>
    );
  }

  return (
    <Row ref={wrapperRef} wid="inherit" pos="relative" style={props.wrapperStyle}>
      <AppWrapper {...props}>
        {title !== '$noTitle' && <Text>{title || t('common.missing')}</Text>}

        <Clickable onClick={props.onClick || handleActive}>
          <Row noFlex centerAll>
            <Text col={optColor || (!selected ? theme.GREY_60 : '#000')}>
              {selected ? getSelected() : props.placeholder || t('common.select')}
            </Text>

            <AppIconContainer active={active}>
              <Icon type="down" dimensions={12} col={theme.GREY_50} />
            </AppIconContainer>
          </Row>
        </Clickable>

        {props.error && (
          <Row noFlex>
            <Text c1 bold marg="4px 0 0 16px" col={theme.SECONDARY}>
              {props.error}
            </Text>
          </Row>
        )}
      </AppWrapper>

      {active && (
        <Col pos="absolute" wid="100%" top="0" left="0">
          <OptionsWrapper {...props}>
            {props.options?.map((x, i) => {
              const isSelected = selectedIdx === i;

              return (
                <Clickable onClick={() => handleClick(x, i)} key={i}>
                  <Row between pad="16px 16px" wid="calc(100% - 32px)" ht="22px">
                    <Text b1 med wid="100%">
                      {getText(x)}
                    </Text>

                    {isSelected && <Icon type="done" col={theme.SECONDARY} />}
                  </Row>
                </Clickable>
              );
            })}
          </OptionsWrapper>
        </Col>
      )}
    </Row>
  );
};

export default DropDown;
